import { useCallback, useRef } from 'react';
import { WYSIWYG } from 'src/components/atoms';
import { TextRange } from 'src/graphql';
import {
  TextDeletionEmptyDivStyled,
  TextDeletionEmptyStyled,
  TextDeletionInputContainerStyled,
} from './TextDeletionInput.styles';

export type FieldWithTextRange =
  | 'description'
  | 'emotion'
  | 'witnesses'
  | 'externalNamedOffenders'
  | 'reporterNotes'
  | 'location';

interface IProps {
  field: FieldWithTextRange;
  value: string;
  onTextRangeDeleteRequested: (
    range: TextRange,
    field: FieldWithTextRange,
    eventId?: string,
  ) => void;
  handleTextFieldClearAll: (
    field: FieldWithTextRange,
    eventId?: string,
  ) => void;
  uniqueKey?: string;
  appliedRanges?: TextRange[];
  eventId?: string;
}

export const TextDeletionInput = ({
  field,
  value,
  onTextRangeDeleteRequested,
  handleTextFieldClearAll,
  uniqueKey,
  appliedRanges,
  eventId,
}: Readonly<IProps>) => {
  const latestPropsRef = useRef({
    onTextRangeDeleteRequested,
    field,
    eventId,
  });
  latestPropsRef.current = {
    onTextRangeDeleteRequested,
    field,
    eventId,
  };

  const onDeleteRequested = useCallback((range: TextRange) => {
    const {
      onTextRangeDeleteRequested: latest,
      field: latestField,
      eventId: latestEventId,
    } = latestPropsRef.current;
    latest(range, latestField, latestEventId);
  }, []);

  const onClearAll = () => {
    handleTextFieldClearAll(field, eventId);
  };

  if (value === '') {
    return (
      <TextDeletionEmptyStyled data-testid="text-deletion-input-empty">
        <TextDeletionEmptyDivStyled>{' - '}</TextDeletionEmptyDivStyled>
      </TextDeletionEmptyStyled>
    );
  }
  return (
    <TextDeletionInputContainerStyled data-testid="text-deletion-input">
      <WYSIWYG
        existingText={value}
        onDeleteRequested={onDeleteRequested}
        onClearAll={onClearAll}
        uniqueKey={uniqueKey}
        appliedRanges={appliedRanges}
      />
    </TextDeletionInputContainerStyled>
  );
};
